import { useEffect, useMemo } from 'react'

import { Typography } from 'antd'

import { GenericalRecord } from '@eastcoal/common/types/utilities'
import { omit, pick } from '@eastcoal/common/utils/object'

import { FormBlock } from '@eastcoal/formily/components/FormBlock'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { RadioGroup } from '@eastcoal/formily/components/Radio'
import { SchemaForm } from '@eastcoal/formily/components/SchemaForm'
import { SchemaFormProps } from '@eastcoal/formily/components/SchemaForm/interface'
import { onFormInputChange } from '@eastcoal/formily/exports/core'
import { useCreationForm } from '@eastcoal/formily/hooks/useCreationForm'

import { $getNodeByKey } from '@eastcoal/editor/exports'
import { useEditor } from '@eastcoal/editor/hooks/useEditor'
import { FontBackgroundColorPicker } from '@eastcoal/editor/ui/FontBackgroundColorPicker'

import { FontColorPicker } from '../../../../@formily/FontColorPicker'
import { FontFamilySelect } from '../../../../@formily/FontFamilySelect'
import { FontSizeSelect } from '../../../../@formily/FontSizeSelect'
import { LineHeightSelect } from '../../../../@formily/LineHeightSelect'
import { $isTableVariableNode } from '../../../../editor/nodes/TableVariableNode'
import { TableVariableNodeVariableChild } from '../../../../editor/nodes/TableVariableNode/interface'
import { useContractDesignerStore } from '../../../Store/hooks/useStore'
import { FontStyleCheckboxGroup } from '../components/FontStyleCheckboxGroup'
import { TextAlignRadioGroup } from '../components/TextAlignRadioGroup'
import { VerticalAlignRadioGroup } from '../components/VerticalAlignRadioGroup'

import { CurrentFieldText } from './CurrentFieldText'
import { FieldCheckboxGroup } from './FieldChecboxGroup'
import { TableVariableConfigureProps } from './interface'

const schema: SchemaFormProps<GenericalRecord>['schema'] = [
  {
    type: 'void',
    title: '表格样式',
    'x-component': FormBlock,
    properties: {
      tableStyleConfig: {
        type: 'object',
        title: (
          <Typography.Title level={5} style={{ margin: 0 }}>
            表格样式
          </Typography.Title>
        ),
        'x-component': FormBlock.Panel,
        properties: {
          lineHeight: {
            type: 'number',
            title: '行高',
            'x-component': LineHeightSelect,
            'x-decorator': FormItem,
            'x-decorator-props': {
              wrapperWidth: 160
            }
          },
          fontfamily: {
            type: 'string',
            title: '字体',
            'x-component': FontFamilySelect,
            'x-decorator': FormItem,
            'x-decorator-props': {
              wrapperWidth: 160
            }
          },
          borderFormat: {
            type: 'string',
            title: '边框格式',
            enum: [
              {
                label: '无边框',
                value: 'none'
              },
              {
                label: '行线',
                value: 'row'
              },
              {
                label: '网格线',
                value: 'cell'
              }
            ],
            'x-component': RadioGroup,
            'x-decorator': FormItem
          },
          textAlign: {
            type: 'string',
            title: '水平对齐',
            'x-component': TextAlignRadioGroup,
            'x-decorator': FormItem
          },
          verticalAlign: {
            type: 'string',
            title: '垂直对齐',
            'x-component': VerticalAlignRadioGroup,
            'x-decorator': FormItem
          }
        }
      },
      headerStyleConfig: {
        type: 'object',
        title: (
          <Typography.Title level={5} style={{ margin: 0 }}>
            表头样式
          </Typography.Title>
        ),
        'x-component': FormBlock.Panel,
        properties: {
          fontSize: {
            type: 'number',
            name: 'fontSize',
            title: '字号',
            'x-component': FontSizeSelect,
            'x-decorator': FormItem,
            'x-decorator-props': {
              wrapperWidth: 160
            }
          },
          fontStyle: {
            type: 'object',
            title: '文本样式',
            'x-component': FontStyleCheckboxGroup,
            'x-decorator': FormItem
          },
          color: {
            type: 'string',
            title: '文本颜色',
            'x-component': FontColorPicker,
            'x-component-props': {
              showText: true,
              placement: 'topLeft'
            },
            'x-decorator': FormItem
          },
          backgroundColor: {
            type: 'string',
            title: '背景颜色',
            'x-component': FontBackgroundColorPicker,
            'x-component-props': {
              showText: true,
              placement: 'topLeft'
            },
            'x-decorator': FormItem
          }
        }
      },
      bodyStyleConfig: {
        type: 'object',
        title: (
          <Typography.Title level={5} style={{ margin: 0 }}>
            内容样式
          </Typography.Title>
        ),
        'x-component': FormBlock.Panel,
        properties: {
          fontSize: {
            type: 'number',
            name: 'fontSize',
            title: '字号',
            'x-component': FontSizeSelect,
            'x-decorator': FormItem,
            'x-decorator-props': {
              wrapperWidth: 160
            }
          },
          color: {
            type: 'string',
            title: '文本颜色',
            'x-component': FontColorPicker,
            'x-component-props': {
              showText: true,
              placement: 'topLeft'
            },
            'x-decorator': FormItem
          }
        }
      },
      columnConfig: {
        type: 'void',
        title: '列配置',
        'x-component': FormBlock.Panel,
        properties: {
          selectedFields: {
            type: 'array',
            title: '展示字段',
            'x-component': FieldCheckboxGroup,
            'x-decorator': FormItem
          }
        }
      },
      currentField: {
        type: 'object',
        title: '字段设置',
        'x-component': FormBlock.Panel,
        properties: {
          name: {
            type: 'string',
            title: '当前字段',
            'x-component': CurrentFieldText,
            'x-decorator': FormItem
          },
          textAlign: {
            type: 'string',
            title: '水平对齐',
            'x-component': TextAlignRadioGroup,
            'x-decorator': FormItem
          },
          fontStyle: {
            type: 'object',
            title: '文本样式',
            'x-component': FontStyleCheckboxGroup,
            'x-decorator': FormItem
          },
          color: {
            type: 'string',
            title: '文本颜色',
            'x-component': FontColorPicker,
            'x-component-props': {
              showText: true,
              placement: 'topLeft'
            },
            'x-decorator': FormItem
          }
        }
      }
    }
  }
]

const grid: SchemaFormProps<GenericalRecord>['grid'] = {
  columns: 1
}

export const TableVariableConfigure = ({
  data
}: TableVariableConfigureProps) => {
  const { activedVariableNodeKey } = useContractDesignerStore(state =>
    pick(state, ['activedVariableNodeKey'])
  )
  const editor = useEditor()
  const tableVariableNode = useMemo(() => {
    if (!activedVariableNodeKey) return undefined
    return editor.getEditorState().read(() => {
      const node = $getNodeByKey(activedVariableNodeKey)
      if ($isTableVariableNode(node)) {
        return node
      }
      return undefined
    })
  }, [activedVariableNodeKey, editor])

  const form = useCreationForm({
    effects() {
      // form 表单变化时，更新表格变量节点的样式配置
      onFormInputChange(form => {
        if (!tableVariableNode) return
        editor.update(() => {
          const {
            tableStyleConfig,
            headerStyleConfig,
            bodyStyleConfig,
            selectedFields,
            currentField
          } = form.values
          tableVariableNode.setTableStyleConfig(tableStyleConfig)
          tableVariableNode.setHeaderStyleConfig({
            ...omit(headerStyleConfig, ['fontStyle']),
            fontWeight: headerStyleConfig.fontStyle.fontWeight
          })
          tableVariableNode.setBodyStyleConfig(bodyStyleConfig)
          // 把 currentField 的配置分别同步到表单的 selectedFields 和 node 节点状态
          const newSelectedFields = selectedFields.map(
            (item: TableVariableNodeVariableChild) => {
              if (item.id === currentField.id) {
                return {
                  ...item,
                  ...omit(currentField, ['fontStyle']),
                  fontWeight: currentField.fontStyle.fontWeight
                }
              }
              return item
            }
          )
          form.setValuesIn('selectedFields', newSelectedFields)
          tableVariableNode.setFields(newSelectedFields)
        })
      })
    }
  })

  // 初始化表格变量节点的样式配置到表单
  useEffect(() => {
    if (!tableVariableNode) return
    const tableStyleConfig = tableVariableNode.getTableStyleConfig()
    const headerStyleConfig = tableVariableNode.getHeaderStyleConfig()
    const bodyStyleConfig = tableVariableNode.getBodyStyleConfig()
    const selectedFields = tableVariableNode.getVariable().children
    const currentField = selectedFields[0]
    form.setInitialValues({
      tableStyleConfig,
      bodyStyleConfig,
      headerStyleConfig: {
        ...omit(headerStyleConfig, ['fontWeight']),
        fontStyle: {
          fontWeight: headerStyleConfig.fontWeight
        }
      },
      selectedFields,
      currentField: {
        ...omit(currentField, ['fontWeight']),
        fontStyle: {
          fontWeight: currentField.fontWeight
        }
      }
    })
  }, [form, tableVariableNode])

  const scope = useMemo(
    () => ({
      globalVariableData: data
    }),
    [data]
  )

  return (
    <SchemaForm
      form={form}
      grid={grid}
      name='textVariableConfigureForm'
      schema={schema}
      scope={scope}
    />
  )
}
